import { Badge, Box, Button, Flex, IconButton, Text, Tooltip, VStack } from "@chakra-ui/react";
import React from "react";
import { FiLock, FiSettings } from "react-icons/fi";
import { Sparkles } from "lucide-react";

/**
 * Match Score Cell Component
 * Displays match score with appropriate UI based on analysis status and user permissions
 * Shows Magic Tailor indicators for applications created via Magic Tailor
 * 
 * @param {Object} props
 * @param {Object} props.application - Application data
 * @param {boolean} props.canAnalyzeNew - Can user analyze new jobs
 * @param {boolean} props.canReconfigure - Can user reconfigure existing analysis
 * @param {boolean} props.isPremium - Is user premium
 * @param {Function} props.onConfigureClick - Handler for configure button click
 * @param {Function} props.onUpgradeClick - Handler for upgrade button click
 * @param {Function} props.onOpenPreferences - Handler for opening preferences modal (Magic Tailor)
 */
const MatchScoreCell = ({
  application,
  canAnalyzeNew,
  canReconfigure,
  isPremium,
  onConfigureClick,
  onUpgradeClick,
  quotaInfo,
  onOpenPreferences,
}) => {
  const { lastAnalyzedAt, matchScore, isMagicTailor } = application;

  // Magic Tailor applications - show indicators immediately
  if (isMagicTailor) {
    return (
      <VStack spacing={2} align="center" py={1}>
        {/* <Text 
          fontSize="10px" 
          color="gray.500" 
          fontWeight="600" 
          textAlign="center"
          letterSpacing="0.5px"
          textTransform="uppercase"
        >
          Auto-generated
        </Text> */}
        <Flex align="center" gap={2} justify="center">
          <Tooltip 
            label="Configure preferences" 
            placement="top" 
            hasArrow
            bg="purple.600"
            color="white"
            fontSize="xs"
          >
            <IconButton
              icon={<FiSettings />}
              size="sm"
              variant="ghost"
              colorScheme="purple"
              onClick={(e) => {
                e.stopPropagation();
                onOpenPreferences?.(application);
              }}
              _hover={{ 
                bg: "purple.50",
                transform: "rotate(90deg)",
              }}
              _active={{ 
                bg: "purple.100",
                transform: "rotate(90deg) scale(0.95)",
              }}
              _focus={{ boxShadow: "0 0 0 3px rgba(128, 90, 213, 0.1)" }}
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              aria-label="Configure preferences"
              borderRadius="md"
            />
          </Tooltip>
          <Badge
            bgGradient="linear(to-r, purple.500, purple.600)"
            color="white"
            variant="solid"
            fontSize="10px"
            px={3}
            py={1}
            borderRadius="full"
            fontWeight="600"
            display="flex"
            alignItems="center"
            gap={1.5}
            boxShadow="0 2px 8px rgba(128, 90, 213, 0.25)"
            _hover={{
              boxShadow: "0 4px 12px rgba(128, 90, 213, 0.35)",
              transform: "translateY(-1px)",
            }}
            transition="all 0.2s ease"
          >
            <Box as={Sparkles} size="11px" />
            <Text as="span" fontSize="10px" fontWeight="600">Magic Tailored</Text>
          </Badge>
        </Flex>
      </VStack>
    );
  }

  // Not analyzed yet
  if (!lastAnalyzedAt) {
    if (canAnalyzeNew) {
      // Has permission to analyze
      return (
        <Tooltip
          label="Click to configure skills and analyze"
          placement="top"
          hasArrow
        >
          <Button
            size="sm"
            variant="outline"
            borderStyle="dashed"
            borderColor="gray.300"
            color="gray.600"
            leftIcon={<FiSettings size="14px" />}
            onClick={() => onConfigureClick(application)}
            _hover={{
              borderColor: "orange.400",
              color: "orange.600",
              borderStyle: "solid",
              transform: "translateY(-1px)",
              bg: "orange.50",
            }}
            _focus={{ boxShadow: "none" }}
            transition="all 0.2s ease"
            fontSize="xs"
            fontWeight="500"
            px={3}
          >
            Configure
          </Button>
        </Tooltip>
      );
    } else {
      // No permission - show upgrade
      return (
        <Tooltip
          label={`You've used ${quotaInfo.used}/${quotaInfo.total} analyses. Upgrade for unlimited.`}
          placement="top"
          hasArrow
        >
          <Button
            size="sm"
            variant="solid"
            bg="purple.500"
            color="white"
            leftIcon={<FiLock size="14px" />}
            onClick={onUpgradeClick}
            _hover={{
              bg: "purple.600",
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(128, 90, 213, 0.4)",
            }}
            _focus={{ boxShadow: "none" }}
            transition="all 0.2s ease"
            fontSize="xs"
            fontWeight="600"
            px={3}
          >
            Upgrade
          </Button>
        </Tooltip>
      );
    }
  }

  // Already analyzed
  if (canReconfigure) {
    // Can reconfigure (premium or has quota)
    if (matchScore === 0) {
      return (
        <Tooltip
          label={`No match found - Analyzed ${new Date(lastAnalyzedAt).toLocaleDateString()} - Click to review`}
          placement="top"
          hasArrow
        >
          <Flex
            as="button"
            align="center"
            gap={2}
            bg="red.100"
            color="red.700"
            px={3}
            py={1.5}
            borderRadius="full"
            fontSize="xs"
            fontWeight="600"
            minW="80px"
            cursor="pointer"
            onClick={() => onConfigureClick(application)}
            _hover={{
              bg: "red.200",
              transform: "translateY(-1px)",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            }}
            transition="all 0.2s ease"
            border="none"
          >
            <Text>0%</Text>
            <Box as={FiSettings} size="12px" />
          </Flex>
        </Tooltip>
      );
    }

    return (
      <Tooltip
        label={`Analyzed ${new Date(lastAnalyzedAt).toLocaleDateString()} - Click to adjust`}
        placement="top"
        hasArrow
      >
        <Flex
          as="button"
          align="center"
          gap={2}
          bg={
            matchScore >= 80
              ? "green.100"
              : matchScore >= 60
                ? "yellow.100"
                : matchScore >= 40
                  ? "orange.100"
                  : "red.100"
          }
          color={
            matchScore >= 80
              ? "green.700"
              : matchScore >= 60
                ? "yellow.700"
                : matchScore >= 40
                  ? "orange.700"
                  : "red.700"
          }
          px={3}
          py={1.5}
          borderRadius="full"
          fontSize="xs"
          fontWeight="600"
          minW="80px"
          cursor="pointer"
          onClick={() => onConfigureClick(application)}
          _hover={{
            transform: "translateY(-1px)",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            opacity: 0.9,
          }}
          transition="all 0.2s ease"
          border="none"
        >
          <Text>{matchScore}%</Text>
          <Box as={FiSettings} size="12px" />
        </Flex>
      </Tooltip>
    );
  }

  // No permission to reconfigure - view only
  const viewOnlyScore = (
    <Tooltip
      label="Upgrade to reconfigure this analysis"
      placement="top"
      hasArrow
    >
      <Flex
        align="center"
        gap={2}
        bg={
          matchScore >= 80
            ? "green.100"
            : matchScore >= 60
              ? "yellow.100"
              : matchScore >= 40
                ? "orange.100"
                : "red.100"
        }
        color={
          matchScore >= 80
            ? "green.700"
            : matchScore >= 60
              ? "yellow.700"
              : matchScore >= 40
                ? "orange.700"
                : "red.700"
        }
        px={3}
        py={1.5}
        borderRadius="full"
        fontSize="xs"
        fontWeight="600"
        minW="80px"
        cursor="not-allowed"
        opacity={0.7}
      >
        <Text>{matchScore}%</Text>
      </Flex>
    </Tooltip>
  );

  return viewOnlyScore;
};

export default MatchScoreCell;
